import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ProfileService } from '@app/core';
import { ShopService } from '@app/core/services/shop.service';
import { Charge } from '@app/models/charge.model';
import { environment } from '@env/environment';
import { firstValueFrom } from 'rxjs';
import {
  loadStripe,
  Stripe,
  StripeElements,
  StripePaymentElement,
} from '@stripe/stripe-js';
import type { PaymentIntentResult, SetupIntentResult } from '@stripe/stripe-js';

@Component({
  selector: 'app-cc-checkout',
  templateUrl: './cc-checkout.component.html',
  styleUrls: ['./cc-checkout.component.scss'],
})
export class CcCheckoutComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() charge: Charge;

  @ViewChild('cardNumberElement', { static: true })
  cardNumberElement: ElementRef;
  @ViewChild('cardExpiryElement', { static: true })
  cardExpiryElement: ElementRef;
  @ViewChild('cardCvcElement', { static: true }) cardCvcElement: ElementRef;

  cardNameOn = '';
  stripe: Stripe | null = null;
  cardNumber;
  cardExpiry;
  cardCvc;
  cardErrors;
  brandClass = 'pf pf-credit-card';
  isEnteringInfo = false;
  clientSecret = undefined;
  subscriptionId: string = undefined;
  intentType: 'payment' | 'setup' = undefined;
  elements: StripeElements | null = null;
  paymentElement: StripePaymentElement | null = null;

  loading = false;
  validCreditCard = false;
  confirmation;

  constructor(private profiles: ProfileService, private shop: ShopService) {}

  async ngOnInit() {
    this.stripe = await loadStripe(environment.stripeKey);

    const method = this.charge.recurring
      ? 'getStripeSubscriptionPaymentIntent'
      : 'getStripePaymentIntent';

    const res: {
      client_secret: string;
      subscription_id: string;
      intent_type: 'payment' | 'setup';
    } = await this.shop[method]({
      amount: this.charge.amount,
      product_id: this.charge.product_id,
      quantity: this.charge.quantity,
      anonymous: this.charge.anonymous ?? false,
      recipient_id: this.charge.recipient_id,
    });

    this.clientSecret = res.client_secret;
    this.subscriptionId = res.subscription_id ?? null;
    this.intentType = res.intent_type;

    if (!this.clientSecret) return console.error('Missing client_secret');

    this.elements = this.stripe.elements({
      clientSecret: this.clientSecret,
    });
    this.paymentElement = this.elements.create('payment', {
      layout: 'tabs',
    });
    this.paymentElement!.mount('#payment-element');
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {}

  private async buildStripeElements() {}

  public async chargeCreditCard(charge: Charge) {
    try {
      this.loading = true;

      // make sure payment processor is set to stripe
      charge.processor = 'stripe';

      let result: PaymentIntentResult | SetupIntentResult | undefined;

      if (this.intentType === 'payment') {
        result = await this.stripe.confirmPayment({
          elements: this.elements,
          redirect: 'if_required',
        });
      } else {
        // 'setup'
        result = await this.stripe.confirmSetup({
          elements: this.elements,
          redirect: 'if_required',
        });
      }

      if (result?.error) {
        console.error(result.error);
        throw new Error(result.error.message ?? 'Stripe confirmation failed');
      }

      const orderPaid = await firstValueFrom(
        this.shop.createAndChargeOrder({
          ...charge,
          token: this.subscriptionId,
        } as any)
      );

      if (!orderPaid.success) throw new Error(orderPaid.message);

      return true;
    } finally {
      this.loading = false;
    }
  }
}

const style = {
  base: {
    color: '#32325d',
    fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
    fontSmoothing: 'antialiased',
    fontSize: '16px',
    '::placeholder': {
      color: '#aab7c4',
    },
  },
  invalid: {
    color: '#fa755a',
    iconColor: '#fa755a',
  },
};

const cardBrandToPfClass = {
  visa: 'pf pf-visa',
  mastercard: 'pf pf-mastercard',
  amex: 'pf pf-american-express',
  discover: 'pf pf-discover',
  diners: 'pf pf-diners',
  jcb: 'pf pf-jcb',
  unknown: 'pf pf-credit-card',
};

function getBrandClass(brand: any): string {
  let pfClass = 'pf pf-credit-card';
  if (brand in cardBrandToPfClass) {
    pfClass = cardBrandToPfClass[brand];
  }
  return pfClass;
}
